import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getExam, getMyResult, getLeaderboard, getResults } from '@/api/exams'
import { useAuth } from '@/context/AuthContext'
import { Chip, Button } from '@/components/aurora'
import { Trophy, RefreshCw, Home, Clock, CheckCircle2, XCircle, MinusCircle, ChevronRight } from 'lucide-react'
import { sanitizeHtml } from '@/utils/sanitize'

// ── Question type labels ──────────────────────────────────────────────
const Q_TYPE = { 1: 'Bir javobli', 2: 'Ko\'p javobli', 3: 'Ha/Yo\'q', 4: 'Qisqa javob', 5: 'Esse', 6: 'Bo\'sh to\'ldirish', 7: 'Moslashtirish' }

// ── Per-question review card ──────────────────────────────────────────
function ReviewItem({ item, index }) {
  const { question_type: type, content, options, student_answer, correct_answer, earned, score, is_correct } = item
  const partial = !is_correct && earned > 0

  const borderColor = is_correct ? 'var(--mint)' : partial ? 'var(--butter)' : 'var(--danger)'
  const bgColor     = is_correct ? 'var(--mint-tint)' : partial ? 'var(--butter-tint)' : 'var(--danger-tint)'

  function optText(id) {
    const opt = options?.find(o => o.id === id || o.id === Number(id))
    return opt?.text ?? `#${id}`
  }

  function renderStudentAnswer() {
    if (student_answer === null || student_answer === undefined) return <span style={{ color: 'var(--ink-3)', fontStyle: 'italic' }}>Javob berilmagan</span>
    if (type === 3) return <span>{student_answer ? 'Ha (True)' : "Yo'q (False)"}</span>
    if (type === 2 && Array.isArray(student_answer)) return student_answer.map(id => optText(id)).join(', ')
    if (type === 1) return optText(student_answer)
    return String(student_answer)
  }

  function renderCorrectAnswer() {
    if (!correct_answer) return null
    if (type === 1) return optText(correct_answer.answer)
    if (type === 2) return (correct_answer.answers ?? []).map(id => optText(id)).join(', ')
    if (type === 3) return correct_answer.answer ? 'Ha (True)' : "Yo'q (False)"
    if (type === 6) return (correct_answer.answers ?? []).join(', ')
    return null
  }

  const correctText = renderCorrectAnswer()

  return (
    <div className="rounded-2xl border overflow-hidden" style={{ borderColor, background: 'var(--surface)' }}>
      {/* Header stripe */}
      <div className="flex items-center justify-between px-4 py-2.5" style={{ background: bgColor, borderBottom: `1px solid ${borderColor}` }}>
        <div className="flex items-center gap-2">
          {is_correct
            ? <CheckCircle2 size={16} style={{ color: 'var(--mint)' }} />
            : partial
              ? <MinusCircle size={16} style={{ color: 'var(--butter)' }} />
              : <XCircle size={16} style={{ color: 'var(--danger)' }} />
          }
          <span className="text-[12px] font-semibold" style={{ color: 'var(--ink-2)' }}>
            {index + 1}-savol · {Q_TYPE[type] ?? ''}
          </span>
        </div>
        <span className="font-mono text-[12px] font-bold" style={{ color: is_correct ? 'var(--mint)' : partial ? 'var(--warn)' : 'var(--danger)' }}>
          {earned}/{score} ball
        </span>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-3">
        <div
          className="text-sm font-medium prose prose-sm dark:prose-invert max-w-none"
          style={{ color: 'var(--ink)' }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
        />

        {/* Options list for type 1 & 2 */}
        {options?.length > 0 && (type === 1 || type === 2) && (
          <ul className="space-y-1.5">
            {options.map((opt, i) => {
              const isStudentPick = type === 1
                ? student_answer === opt.id || student_answer === String(opt.id)
                : Array.isArray(student_answer) && (student_answer.includes(opt.id) || student_answer.includes(String(opt.id)))
              const isCorrectPick = type === 1
                ? correct_answer?.answer === opt.id
                : (correct_answer?.answers ?? []).includes(opt.id)

              let bg = 'var(--surface-2)', border = 'var(--border)', color = 'var(--ink-2)'
              if (isCorrectPick) { bg = 'var(--mint-tint)'; border = 'var(--mint-soft)'; color = 'var(--success)' }
              if (isStudentPick && !isCorrectPick) { bg = 'var(--danger-tint)'; border = 'var(--danger-soft)'; color = 'var(--danger)' }

              return (
                <li key={i} className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm border" style={{ background: bg, borderColor: border, color }}>
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: isCorrectPick ? 'var(--mint)' : isStudentPick ? 'var(--danger)' : 'var(--bg-soft)', color: (isCorrectPick || isStudentPick) ? '#fff' : 'var(--ink-3)' }}>
                    {isCorrectPick ? '✓' : isStudentPick ? '✗' : opt.id}
                  </span>
                  {opt.text}
                  {isStudentPick && isCorrectPick && <span className="ml-auto text-[11px] font-semibold" style={{ color: 'var(--mint)' }}>Siz · To'g'ri</span>}
                  {isStudentPick && !isCorrectPick && <span className="ml-auto text-[11px] font-semibold" style={{ color: 'var(--danger)' }}>Sizning javobingiz</span>}
                  {!isStudentPick && isCorrectPick && <span className="ml-auto text-[11px] font-semibold" style={{ color: 'var(--mint)' }}>To'g'ri javob</span>}
                </li>
              )
            })}
          </ul>
        )}

        {/* Text answer types */}
        {(type === 3 || type === 4 || type === 5 || type === 6) && (
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl px-3 py-2 border" style={{ background: 'var(--danger-tint)', borderColor: 'var(--danger-soft)' }}>
              <p className="text-[10px] font-semibold uppercase mb-1" style={{ color: 'var(--ink-3)' }}>Sizning javobingiz</p>
              <p className="text-sm font-medium m-0" style={{ color: 'var(--danger)' }}>{renderStudentAnswer()}</p>
            </div>
            {correctText && (
              <div className="rounded-xl px-3 py-2 border" style={{ background: 'var(--mint-tint)', borderColor: 'var(--mint-soft)' }}>
                <p className="text-[10px] font-semibold uppercase mb-1" style={{ color: 'var(--ink-3)' }}>To'g'ri javob</p>
                <p className="text-sm font-medium m-0" style={{ color: 'var(--success)' }}>{correctText}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function fmt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// ── Attempt review modal ──────────────────────────────────────────────
function AttemptReviewModal({ examId, resultId, onClose }) {
  const [data,    setData]    = useState(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)

  useEffect(() => {
    getMyResult(examId, resultId)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false))
  }, [examId, resultId])

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
        <div
          className="w-full max-w-lg rounded-2xl border flex flex-col shadow-xl overflow-hidden"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)', maxHeight: '85vh' }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
            <h3 className="font-bold text-base m-0" style={{ color: 'var(--ink)' }}>🔍 Xatolarni ko'rish</h3>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-lg"
              style={{ color: 'var(--ink-3)' }}
            >✕</button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {loading && (
              <div className="flex justify-center py-12">
                <div className="w-7 h-7 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--border)', borderTopColor: 'var(--coral)' }}/>
              </div>
            )}
            {error && <p className="text-sm text-center py-8" style={{ color: 'var(--danger)' }}>{error}</p>}
            {data && !data.review && (
              <p className="text-sm text-center py-8" style={{ color: 'var(--ink-3)' }}>
                Bu imtihon uchun xatolarni ko'rish yoqilmagan
              </p>
            )}
            {data?.review?.length > 0 && (
              <div className="space-y-3">
                {data.review.map((item, i) => (
                  <ReviewItem key={item.id} item={item} index={i} />
                ))}
              </div>
            )}
            {data?.review?.length === 0 && (
              <p className="text-sm text-center py-8" style={{ color: 'var(--ink-3)' }}>
                Barcha savollarga to'g'ri javob berdingiz! 🎉
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default function ExamResultPage() {
  const { code }   = useParams()
  const id = code
  const navigate   = useNavigate()
  const { t }      = useTranslation()
  const { isAuth, isLoading: authLoading, user } = useAuth()

  const [exam,       setExam]       = useState(null)
  const [result,     setResult]     = useState(null)
  const [board,      setBoard]      = useState(null)
  const [attempts,   setAttempts]   = useState([])
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState(null)
  const [reviewId,   setReviewId]   = useState(null)

  const fetchedRef  = useRef(false)
  const pollRef     = useRef(null)

  const loadResults = (isInitial = false) => {
    Promise.all([
      isInitial ? getExam(id) : Promise.resolve(null),
      getResults(id),
    ])
      .then(([examRes, attemptsRes]) => {
        if (examRes) setExam(examRes.data)
        const raw = Array.isArray(attemptsRes.data)
          ? attemptsRes.data
          : (attemptsRes.data?.results ?? [])
        // Teachers/admins get every student's attempts from this endpoint
        // (see ExamViewSet.results); the student-facing serializer omits
        // `student` entirely, so its absence already means "this is mine".
        const list = raw.filter(a => !a.student || a.student.id === user?.id)
        const sorted = list.sort((a, b) => new Date(b.finished_at) - new Date(a.finished_at))
        setAttempts(sorted)
        setResult(sorted[0] ?? null)

        // Poll every 5s while latest attempt still has pending AI grading
        clearInterval(pollRef.current)
        if (sorted[0]?.pending_ai_grading) {
          pollRef.current = setInterval(() => loadResults(false), 5000)
        }

        if (isInitial) {
          const examData = examRes?.data
          if (examData?.leaderboard_visible)
            getLeaderboard(id).then(({ data }) => setBoard(data.entries ?? data))
        }
      })
      .catch(e => { if (isInitial) setError(e.response?.data?.detail || e.message) })
      .finally(() => { if (isInitial) setLoading(false) })
  }

  useEffect(() => {
    if (authLoading) return
    if (!isAuth) { navigate('/'); return }
    if (fetchedRef.current) return
    fetchedRef.current = true
    loadResults(true)
    return () => clearInterval(pollRef.current)
  }, [id, isAuth, authLoading, navigate])

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--border)', borderTopColor: 'var(--coral)' }}/>
    </div>
  )

  if (error) return (
    <div className="flex items-center justify-center px-6 py-24">
      <div className="text-center p-8 max-w-sm w-full rounded-2xl border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <p className="mb-4 text-sm" style={{ color: 'var(--danger)' }}>{error}</p>
        <Button onClick={() => navigate('/')}>
          <Home size={14} strokeWidth={2}/> {t('common.home', 'Bosh sahifa')}
        </Button>
      </div>
    </div>
  )

  const score       = Math.round(parseFloat(result?.total_score ?? 0) * 100) / 100
  const maxScore    = Math.round(parseFloat(result?.max_score ?? 0) * 100) / 100
  const grade       = result?.grade_label
  const gradePoints = result?.grade_point
  const doneAt      = result?.finished_at
  const pct         = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0
  const pendingAi   = result?.pending_ai_grading ?? false

  return (
    <div className="px-4 py-8">
      <div className="max-w-2xl mx-auto flex flex-col gap-4">

        {/* Exam title */}
        <h1 className="text-xl font-bold m-0" style={{ color: 'var(--ink)' }}>{exam?.title}</h1>

        {/* Score hero */}
        <div
          className="rounded-2xl px-6 py-8 text-center border"
          style={pendingAi
            ? { background: 'var(--lav-tint)', borderColor: 'var(--lav-soft)' }
            : { background: 'var(--coral-tint)', borderColor: 'var(--coral-soft)' }}
        >
          <p className="text-[12px] uppercase tracking-wider m-0 mb-2 font-semibold" style={{ color: 'var(--ink-3)' }}>
            {t('result.yourScore', 'Sizning natijangiz')}
          </p>
          {pendingAi ? (
            <div className="my-3 flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 rounded-full animate-spin"
                style={{ borderColor: 'var(--lav-soft)', borderTopColor: 'var(--lav)' }} />
              <p className="text-xl font-bold m-0" style={{ color: 'var(--lav)' }}>Tekshirilmoqda...</p>
            </div>
          ) : (
            <div className="text-[72px] font-black tracking-tight leading-none my-3" style={{ color: 'var(--coral)' }}>
              {score}
            </div>
          )}
          {!pendingAi && maxScore > 0 && (
            <p className="text-sm mb-3" style={{ color: 'var(--ink-2)' }}>
              {maxScore} ball ichidan · {pct}%
            </p>
          )}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {grade       && <Chip variant="grade">{grade}</Chip>}
            {gradePoints && <Chip variant="score">{gradePoints} GPA</Chip>}
          </div>
          {doneAt && (
            <p className="text-[11px] mt-3 flex items-center justify-center gap-1.5" style={{ color: 'var(--ink-3)' }}>
              <Clock size={11} strokeWidth={2}/> {fmt(doneAt)}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="flex-1">
            <Home size={14} strokeWidth={2}/> {t('common.home', 'Bosh sahifa')}
          </Button>
          {exam?.leaderboard_visible && (
            <Button onClick={() => navigate(`/exam/${id}/leaderboard`)} variant="gold" className="flex-1">
              <Trophy size={14} strokeWidth={2}/> {t('exams.leaderboard', 'Reyting')}
            </Button>
          )}
          {exam?.allowed_attempts > 1 && (
            <Button onClick={() => navigate(`/exam/${id}`)} variant="primary" className="flex-1">
              <RefreshCw size={14} strokeWidth={2}/> {t('exams.retake', 'Qayta topshirish')}
            </Button>
          )}
        </div>

        {/* My attempts list */}
        {attempts.length > 0 && (
          <div className="rounded-2xl border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
              <span className="text-[12.5px] font-semibold uppercase tracking-wider" style={{ color: 'var(--ink-3)' }}>
                Urinishlarim
              </span>
              <span className="text-[12px]" style={{ color: 'var(--ink-3)' }}>{attempts.length} ta</span>
            </div>
            <div className="divide-y" style={{ '--tw-divide-opacity': 1 }}>
              {attempts.map((att, i) => {
                const attScore  = parseFloat(att.total_score ?? 0)
                const attMax    = parseFloat(att.max_score ?? 0)
                const attPct    = attMax > 0 ? Math.round((attScore / attMax) * 100) : 0
                const canReview = exam?.show_errors
                return (
                  <div
                    key={att.id}
                    onClick={canReview ? () => setReviewId(att.id) : undefined}
                    className="flex items-center gap-3 px-5 py-3.5 transition-all"
                    style={{
                      borderColor: 'var(--border)',
                      cursor: canReview ? 'pointer' : 'default',
                      ...(canReview ? { ':hover': { background: 'var(--surface-2)' } } : {}),
                    }}
                  >
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                      style={{ background: 'var(--bg-soft)', color: 'var(--ink-3)' }}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold m-0" style={{ color: 'var(--ink)' }}>
                        {attMax > 0
                          ? <>{attScore} / {attMax} ball <span className="font-normal ml-2" style={{ color: 'var(--ink-3)' }}>({attPct}%)</span></>
                          : <>{attScore} ball</>}
                        {att.pending_ai_grading && (
                          <span className="ml-2 text-[11px] font-medium" style={{ color: 'var(--lav)' }}>
                            🤖 tekshirilmoqda...
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] m-0 flex items-center gap-1" style={{ color: 'var(--ink-3)' }}>
                        <Clock size={10} strokeWidth={2}/> {fmt(att.finished_at)}
                      </p>
                    </div>
                    {att.grade_label && <Chip variant="grade">{att.grade_label}</Chip>}
                    {canReview && (
                      <span className="flex items-center gap-1 text-[12px] font-medium" style={{ color: 'var(--lav)' }}>
                        🔍 <ChevronRight size={14} strokeWidth={2}/>
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
            {!exam?.show_errors && (
              <div className="px-5 py-3 border-t text-[11px] text-center" style={{ borderColor: 'var(--border)', color: 'var(--ink-3)' }}>
                Xatolarni ko'rish o'qituvchi tomonidan yoqilmagan
              </div>
            )}
          </div>
        )}

        {/* Mini leaderboard preview */}
        {board && board.length > 0 && (
          <div
            className="rounded-2xl border px-5 py-5"
            style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <span
                className="text-[12.5px] font-semibold uppercase tracking-wider flex items-center gap-1.5"
                style={{ color: 'var(--ink-3)' }}
              >
                <Trophy size={13} strokeWidth={2}/> {t('exams.leaderboard', 'Reyting')}
              </span>
              <Link
                to={`/exam/${id}/leaderboard`}
                className="text-[12.5px]"
                style={{ color: 'var(--lav)', textDecoration: 'none' }}
              >
                {t('common.all', 'Hammasi')} →
              </Link>
            </div>
            <div className="flex flex-col gap-2">
              {board.slice(0, 5).map((row, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl border"
                  style={row.my_result
                    ? { background: 'var(--coral-tint)', borderColor: 'var(--coral-soft)' }
                    : { background: 'var(--bg)', borderColor: 'var(--border)' }
                  }
                >
                  <span className="font-mono text-[13px] w-6 text-center" style={{ color: 'var(--ink-3)' }}>{i + 1}</span>
                  <span className="flex-1 text-[13px] font-medium truncate" style={{ color: 'var(--ink)' }}>{row.student_name}</span>
                  <span className="font-mono text-[12px]" style={{ color: 'var(--ink-2)' }}>{row.total_score}</span>
                  {row.my_result && <Chip variant="new">Siz</Chip>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attempt review modal */}
        {reviewId && (
          <AttemptReviewModal
            examId={id}
            resultId={reviewId}
            onClose={() => setReviewId(null)}
          />
        )}
      </div>
    </div>
  )
}
